// ============================================================
// A11yGate.tsx — A11y Gate™ Accessibility Audit System
// Route: /a11y  (add to main.tsx)
// Four products: Starter Kit, Design Bundle, Professional Toolkit, Studio License
// V 10-section structure
// ISO: 2026-05-06
// ============================================================

import { Link } from "react-router-dom";

const SE = {
  cream:  "var(--se-cream, #f9f6f1)",
  black:  "var(--se-black, #1a1a1a)",
  gold:   "var(--se-gold, #d6b168)",
  soft:   "var(--se-soft-gray, #e6e2dc)",
  mid:    "var(--se-mid-gray, #b8b3ac)",
  deep:   "var(--se-deep-charcoal, #2a2a2a)",
  serif:  "var(--font-serif, 'Cormorant Garamond', Georgia, serif)",
  sans:   "var(--font-sans, 'Inter', system-ui, sans-serif)",
  mono:   "var(--font-mono, 'IBM Plex Mono', monospace)",
  a11y:   "#5a7fc4",   // A11y Gate accent — distinct from Caretide + Game
};

const TIERS = [
  {
    key:      "a11y_starter_kit",
    tier:     "STARTER KIT",
    name:     "A11y Gate™ — Starter Kit",
    price:    "See plans",
    desc:     "The foundation for accessibility auditing. WCAG 2.2 compliance checklist, component-level review template, and remediation priority framework.",
    items:    [
      "WCAG 2.2 A/AA compliance checklist",
      "Component-level audit template",
      "Remediation priority scoring",
      "Screen reader compatibility review",
      "Color contrast and typography audit",
    ],
    cta:      "Get Starter Kit",
    stripeKey: "a11y_starter_kit",
  },
  {
    key:      "a11y_design_bundle",
    tier:     "DESIGN BUNDLE",
    name:     "A11y Gate™ — Design Bundle",
    price:    "$29",
    desc:     "Accessibility audit toolkit for design teams. Covers Figma component review, design system token compliance, and handoff documentation.",
    items:    [
      "All Starter Kit features",
      "Figma component accessibility review",
      "Design system token compliance audit",
      "Accessible handoff documentation",
      "Focus state and interaction audit",
    ],
    cta:      "Get Design Bundle",
    stripeKey: "a11y_design_bundle",
  },
  {
    key:      "a11y_professional_toolkit",
    tier:     "PROFESSIONAL TOOLKIT",
    name:     "A11y Gate™ — Professional Toolkit",
    price:    "$49",
    desc:     "Full accessibility audit system for professional developers and QA teams. Covers code-level review, automated testing integration, and documentation.",
    items:    [
      "All Design Bundle features",
      "Code-level accessibility audit",
      "Automated testing integration guide",
      "ARIA implementation review",
      "Keyboard navigation audit",
      "Accessibility statement template",
    ],
    cta:      "Get Professional Toolkit",
    stripeKey: "a11y_professional_toolkit",
    featured: true,
  },
  {
    key:      "a11y_studio_license",
    tier:     "STUDIO LICENSE",
    name:     "A11y Gate™ — Studio License",
    price:    "See plans",
    desc:     "Studio-level accessibility compliance for agencies and multi-project teams. Recurring access, team seat management, and priority support.",
    items:    [
      "All Professional Toolkit features",
      "Multi-project audit tracking",
      "Team seat management",
      "Priority support",
      "Quarterly WCAG update notifications",
      "Agency client reporting templates",
    ],
    cta:      "Get Studio License",
    stripeKey: "a11y_studio_license",
  },
];

const COVERAGE = [
  { area: "Perceivable",    items: "Text alternatives, time-based media, adaptable content, distinguishable presentation" },
  { area: "Operable",       items: "Keyboard accessible, enough time, seizure safe, navigable, input modalities" },
  { area: "Understandable", items: "Readable, predictable, input assistance" },
  { area: "Robust",         items: "Compatible with current and future assistive technologies" },
];

export default function A11yGate() {
  return (
    <div style={{ background: SE.cream, fontFamily: SE.sans, color: SE.black }}>

      {/* ── NAV ──────────────────────────────────────────────────────── */}
      <nav style={{
        background: SE.black, padding: "0 32px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        height: "52px", borderBottom: `1px solid ${SE.deep}`,
      }}>
        <Link to="/" style={{
          fontFamily: SE.mono, fontSize: "0.65rem", letterSpacing: "0.15em",
          color: SE.gold, textDecoration: "none", textTransform: "uppercase",
        }}>
          ← ELLARI
        </Link>
        <span style={{
          fontFamily: SE.mono, fontSize: "0.65rem", letterSpacing: "0.14em",
          color: SE.mid, textTransform: "uppercase",
        }}>
          A11y Gate™
        </span>
        <a href="https://shopellari.com" style={{
          fontFamily: SE.sans, fontSize: "0.78rem",
          color: SE.mid, textDecoration: "none",
        }}>
          ShopEllari →
        </a>
      </nav>

      {/* ── §1: NAME + VERDICT ───────────────────────────────────────── */}
      <section style={{
        background: SE.black, color: SE.cream,
        padding: "88px 32px 72px", textAlign: "center",
      }}>
        <p style={{
          fontFamily: SE.mono, fontSize: "0.6rem", letterSpacing: "0.2em",
          color: SE.a11y, textTransform: "uppercase", marginBottom: "16px",
        }}>
          AN ELLARI VENTURES SYSTEM — ACCESSIBILITY
        </p>
        <h1 style={{
          fontFamily: SE.serif, fontSize: "clamp(2.4rem, 5vw, 3.8rem)",
          fontWeight: 300, maxWidth: "760px", margin: "0 auto 20px", lineHeight: 1.15,
        }}>
          A11y Gate™
        </h1>
        <p style={{
          fontFamily: SE.mono, fontSize: "0.75rem", color: SE.a11y,
          letterSpacing: "0.12em", marginBottom: "20px", textTransform: "uppercase",
        }}>
          WCAG 2.2 COMPLIANCE AUDIT SYSTEM FOR DESIGN AND DEVELOPMENT TEAMS.
        </p>
        <p style={{
          color: SE.mid, fontSize: "1rem", maxWidth: "580px",
          margin: "0 auto 40px", lineHeight: 1.75,
        }}>
          Accessibility compliance is structured. Most teams fail audits not because
          they don't care — but because no one mapped which components need what standard,
          at which priority, with which evidence. A11y Gate maps it.
        </p>
        <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
          <a href="#plans" style={{
            background: SE.a11y, color: SE.cream,
            padding: "14px 36px", fontFamily: SE.sans, fontWeight: 700,
            fontSize: "0.875rem", textDecoration: "none",
            borderRadius: "2px", letterSpacing: "0.05em",
          }}>
            See Plans →
          </a>
          <a href="#coverage" style={{
            border: `1px solid ${SE.mid}`, color: SE.cream,
            padding: "14px 36px", fontFamily: SE.sans, fontSize: "0.875rem",
            textDecoration: "none", borderRadius: "2px", letterSpacing: "0.04em",
          }}>
            What It Covers
          </a>
        </div>
      </section>

      <Divider />

      {/* ── §2: WHO IT IS BUILT FOR ──────────────────────────────────── */}
      <section style={{ padding: "64px 32px", maxWidth: "900px", margin: "0 auto" }}>
        <Label label="WHO IT IS BUILT FOR" color={SE.a11y} />
        <h2 style={{ fontFamily: SE.serif, fontSize: "clamp(1.4rem, 2.8vw, 2rem)", fontWeight: 400, margin: "16px 0 24px" }}>
          Design teams, developers, and agencies who need audit structure — not just a checklist.
        </h2>
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
          gap: "16px", marginTop: "8px",
        }}>
          {[
            "Frontend developers preparing for WCAG 2.2 audit",
            "Design teams building accessible component systems",
            "QA engineers running accessibility regression testing",
            "Agencies delivering accessible work to clients",
            "Product teams under ADA or Section 508 compliance requirements",
          ].map((who, i) => (
            <div key={i} style={{
              padding: "16px 18px", background: "#fff",
              border: `1px solid ${SE.soft}`, borderRadius: "4px",
              fontSize: "0.83rem", lineHeight: 1.5,
            }}>
              <span style={{ fontFamily: SE.mono, color: SE.a11y, marginRight: "8px" }}>◆</span>
              {who}
            </div>
          ))}
        </div>
      </section>

      <Divider />

      {/* ── §3: COVERAGE ─────────────────────────────────────────────── */}
      <section id="coverage" style={{ background: "#fff", padding: "64px 32px" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <Label label="WCAG 2.2 COVERAGE" color={SE.a11y} />
          <h2 style={{ fontFamily: SE.serif, fontSize: "clamp(1.4rem, 2.8vw, 2rem)", fontWeight: 400, margin: "16px 0 36px" }}>
            Four principles. Every success criterion mapped.
          </h2>
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
            gap: "20px",
          }}>
            {COVERAGE.map(({ area, items }) => (
              <div key={area} style={{
                padding: "24px", border: `1px solid ${SE.soft}`,
                borderTop: `3px solid ${SE.a11y}`, borderRadius: "4px",
              }}>
                <div style={{
                  fontFamily: SE.mono, fontSize: "0.65rem", letterSpacing: "0.12em",
                  color: SE.a11y, marginBottom: "10px", textTransform: "uppercase",
                }}>
                  {area}
                </div>
                <div style={{ fontSize: "0.78rem", color: SE.mid, lineHeight: 1.65 }}>
                  {items}
                </div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: "0.82rem", color: SE.mid, marginTop: "24px", lineHeight: 1.65, maxWidth: "680px" }}>
            A11y Gate maps every WCAG 2.2 criterion against your component inventory.
            Each criterion is assigned a remediation priority score (critical / major / minor)
            based on user impact, legal exposure, and implementation cost.
          </p>
        </div>
      </section>

      <Divider />

      {/* ── §4: PLANS ────────────────────────────────────────────────── */}
      <section id="plans" style={{ padding: "72px 32px", maxWidth: "1200px", margin: "0 auto" }}>
        <Label label="PLANS" color={SE.a11y} />
        <h2 style={{ fontFamily: SE.serif, fontSize: "clamp(1.4rem, 2.8vw, 2rem)", fontWeight: 400, margin: "16px 0 8px" }}>
          Start with Starter Kit. Scale to Studio for ongoing compliance.
        </h2>
        <p style={{ color: SE.mid, fontSize: "0.88rem", marginBottom: "44px", maxWidth: "560px" }}>
          Each tier builds on the last. Professional Toolkit covers the full audit cycle
          for a single project. Studio License covers your whole team, ongoing.
        </p>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          gap: "20px",
        }}>
          {TIERS.map(tier => (
            <div key={tier.key} style={{
              background: tier.featured ? SE.black : "#fff",
              border: `2px solid ${tier.featured ? SE.a11y : SE.soft}`,
              borderRadius: "4px", padding: "28px",
              position: "relative",
            }}>
              {tier.featured && (
                <div style={{
                  position: "absolute", top: "14px", right: "14px",
                  background: SE.a11y, color: SE.cream,
                  fontFamily: SE.mono, fontSize: "0.55rem",
                  letterSpacing: "0.12em", padding: "4px 10px", textTransform: "uppercase",
                }}>
                  RECOMMENDED
                </div>
              )}
              <div style={{
                fontFamily: SE.mono, fontSize: "0.58rem", color: SE.a11y,
                letterSpacing: "0.14em", marginBottom: "10px", textTransform: "uppercase",
              }}>
                {tier.tier}
              </div>
              <div style={{
                fontFamily: SE.serif, fontSize: "1.05rem",
                color: tier.featured ? SE.cream : SE.black, marginBottom: "4px",
              }}>
                {tier.name}
              </div>
              <div style={{
                fontFamily: SE.mono, fontSize: "1rem",
                color: tier.featured ? SE.cream : SE.black, marginBottom: "10px",
              }}>
                {tier.price}
              </div>
              <p style={{
                fontSize: "0.8rem", lineHeight: 1.65, color: SE.mid, marginBottom: "20px",
              }}>
                {tier.desc}
              </p>
              <ul style={{ listStyle: "none", padding: 0, margin: "0 0 24px", display: "flex", flexDirection: "column", gap: "7px" }}>
                {tier.items.map((item, i) => (
                  <li key={i} style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                    <span style={{ fontFamily: SE.mono, color: SE.a11y, fontSize: "0.65rem", marginTop: "2px", flexShrink: 0 }}>◆</span>
                    <span style={{ fontSize: "0.77rem", color: SE.mid, lineHeight: 1.5 }}>{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={`/checkout/${tier.stripeKey}`}
                style={{
                  display: "block", textAlign: "center",
                  background: tier.featured ? SE.a11y : SE.black,
                  color: SE.cream,
                  padding: "12px 20px", fontFamily: SE.sans, fontWeight: 700,
                  fontSize: "0.82rem", textDecoration: "none",
                  borderRadius: "2px", letterSpacing: "0.04em",
                }}
              >
                {tier.cta}
              </Link>
            </div>
          ))}
        </div>
      </section>

      <Divider />

      {/* ── §5: NOT FOR YOU IF ───────────────────────────────────────── */}
      <section style={{ padding: "60px 32px", maxWidth: "760px", margin: "0 auto" }}>
        <Label label="NOT FOR YOU IF" color={SE.a11y} />
        <div style={{ marginTop: "20px", display: "flex", flexDirection: "column", gap: "10px" }}>
          {[
            "You need a fully automated WCAG scanner — A11y Gate is a structured audit system, not a bot",
            "Your project has no frontend components — this covers UI-layer accessibility, not backend",
            "You want one-click compliance certification — audit is not the same as certification",
            "You are looking for legal representation — A11y Gate is an audit tool, not legal counsel",
          ].map((item, i) => (
            <div key={i} style={{
              display: "flex", gap: "12px", padding: "14px 16px",
              background: "#fff", border: `1px solid ${SE.soft}`, borderRadius: "4px",
            }}>
              <span style={{ fontFamily: SE.mono, color: SE.mid, fontSize: "0.75rem", marginTop: "2px", flexShrink: 0 }}>×</span>
              <span style={{ fontSize: "0.83rem", color: SE.deep, lineHeight: 1.55 }}>{item}</span>
            </div>
          ))}
        </div>
      </section>

      <Divider />

      {/* ── §6: SYSTEM CONTEXT ───────────────────────────────────────── */}
      <section style={{ background: SE.black, color: SE.cream, padding: "60px 32px" }}>
        <div style={{ maxWidth: "760px", margin: "0 auto" }}>
          <Label label="SYSTEM CONTEXT" color={SE.a11y} dark />
          <h2 style={{ fontFamily: SE.serif, fontSize: "clamp(1.3rem, 2.5vw, 1.8rem)", fontWeight: 400, margin: "16px 0 16px" }}>
            A11y Gate™ is part of the ELLARI product ecosystem.
          </h2>
          <p style={{ color: SE.mid, lineHeight: 1.75, fontSize: "0.88rem", marginBottom: "24px" }}>
            Built alongside Catch The Pattern™ (formal logic training), Inspection Defense™
            (regulatory language analysis), and Citation Calm™ (director-facing compliance tools).
            Every ELLARI product is a structured analysis system — not an assistant that does
            the work for you.
          </p>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <Link to="/" style={{
              border: `1px solid ${SE.a11y}`, color: SE.a11y,
              padding: "11px 24px", fontFamily: SE.sans, fontWeight: 600,
              fontSize: "0.82rem", textDecoration: "none",
              borderRadius: "2px", letterSpacing: "0.04em",
            }}>
              Catch The Pattern™ →
            </Link>
            <a href="https://shopellari.com" style={{
              border: `1px solid ${SE.mid}`, color: SE.mid,
              padding: "11px 24px", fontFamily: SE.sans, fontSize: "0.82rem",
              textDecoration: "none", borderRadius: "2px", letterSpacing: "0.04em",
            }}>
              ShopEllari →
            </a>
          </div>
        </div>
      </section>

      {/* ── §7: CTA ──────────────────────────────────────────────────── */}
      <section style={{ padding: "80px 32px", textAlign: "center", background: SE.cream }}>
        <p style={{
          fontFamily: SE.mono, fontSize: "0.62rem", letterSpacing: "0.18em",
          color: SE.a11y, marginBottom: "16px", textTransform: "uppercase",
        }}>
          BEGIN
        </p>
        <h2 style={{
          fontFamily: SE.serif, fontSize: "clamp(1.8rem, 4vw, 2.6rem)",
          fontWeight: 400, maxWidth: "560px", margin: "0 auto 12px",
        }}>
          Accessibility compliance has a structure. Now you will see it.
        </h2>
        <p style={{ color: SE.mid, fontSize: "0.88rem", marginBottom: "32px" }}>
          Starting at $29 one-time.
        </p>
        <a href="#plans" style={{
          background: SE.a11y, color: SE.cream,
          padding: "16px 48px", fontFamily: SE.sans, fontWeight: 700,
          fontSize: "0.875rem", textDecoration: "none",
          borderRadius: "2px", letterSpacing: "0.06em",
          display: "inline-block",
        }}>
          Choose A Plan →
        </a>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────────────── */}
      <footer style={{
        background: SE.deep, padding: "24px 32px",
        display: "flex", justifyContent: "space-between",
        alignItems: "center", flexWrap: "wrap", gap: "12px",
      }}>
        <span style={{ fontFamily: SE.mono, fontSize: "0.6rem", color: SE.mid, letterSpacing: "0.1em" }}>
          A11Y GATE™ — AN ELLARI VENTURES SYSTEM
        </span>
        <div style={{ display: "flex", gap: "20px" }}>
          {[
            { label: "Catch The Pattern™", href: "/" },
            { label: "Inspection Defense™", href: "/inspection-defense" },
            { label: "ShopEllari", href: "https://shopellari.com" },
          ].map(link => (
            <a key={link.label} href={link.href} style={{
              fontFamily: SE.sans, fontSize: "0.73rem",
              color: SE.mid, textDecoration: "none",
            }}>
              {link.label}
            </a>
          ))}
        </div>
      </footer>

    </div>
  );
}

function Label({ label, color = "var(--se-gold, #d6b168)", dark }: { label: string; color?: string; dark?: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      <div style={{ height: "1px", width: "20px", background: color }} />
      <span style={{
        fontFamily: "var(--font-mono, monospace)", fontSize: "0.6rem",
        letterSpacing: "0.18em", color, textTransform: "uppercase",
      }}>
        {label}
      </span>
    </div>
  );
}

function Divider() {
  return <div style={{ height: "1px", background: "var(--se-gold, #d6b168)", opacity: 0.2, margin: "0 32px" }} />;
}
